'use client'

import type { Step } from '@polaris/core/services'
import { useAtomValue } from 'jotai'
import { useMemo } from 'react'
import {
  getTradeStepRowAtom,
  isUserRejectionErrorAtom,
  routeAtom,
  routeMachineSnapshotAtom,
} from '@/src/stores/route-machine/atoms'
import { ChainImg } from '../../ui/chain-img'
import TokenImg from '../../ui/token-img'
import { BasicStepRow, BasicStepRowTitle } from './basic-step-row'

interface TitleProps {
  step: Step
  isError?: boolean
}

const Title = ({ step, isError }: TitleProps) => {
  const isSwap = step.general_type === 'Swap'
  const isBridgeSwap = step.general_type === 'BridgeSwap'

  if (isBridgeSwap || isSwap) {
    return (
      <BasicStepRowTitle
        title={
          <div>
            <div className='flex flex-wrap items-center gap-1'>
              Swap to{' '}
              <TokenImg
                ticker={step.to_token.ticker}
                src={step.to_token.logo_img_url}
                width={20}
                height={20}
              />{' '}
              <span className='font-semibold'>{step.to_token.ticker}</span> on{' '}
              {step.to_chain.chain_name}{' '}
              {isBridgeSwap && (
                <p className='flex flex-wrap items-center gap-1'>
                  via{' '}
                  <span className='first-letter:uppercase'>
                    {step.provider}
                  </span>
                </p>
              )}
            </div>
            {isError ? (
              <span className='text-danger-text text-sm leading-6'>
                Trade failed
              </span>
            ) : null}
          </div>
        }
      />
    )
  }

  return (
    <BasicStepRowTitle
      title={
        <div>
          <div className='flex flex-wrap items-center gap-1'>
            Bridge to{' '}
            <ChainImg
              chainId={step.to_chain.chain_id}
              chainImg={step.to_chain.chain_img}
              chainName={step.to_chain.chain_name}
              size='sm'
              containerClassName='size-5 min-w-5'
              width={12.5}
              height={12.5}
              className='size-[12.5px]'
            />{' '}
            <span className='font-semibold'>{step.to_chain.chain_name}</span>{' '}
            via {step.from_chain.chain_name}
          </div>
          {isError ? (
            <span className='text-danger-text text-sm leading-6'>
              Trade failed
            </span>
          ) : null}
        </div>
      }
    />
  )
}

interface StepRowProps {
  step: Step
  className?: string
}

export const StepRow = ({ step, className }: StepRowProps) => {
  /**
   * We create a new atom on demand to avoid unnecessary re-renders,
   * also we can have local state related to the step,
   * we can do it also using atomFamily, but it can create some memory leaks
   */
  const tradeStepAtom = useMemo(() => getTradeStepRowAtom(step), [step])
  const tradeStep = useAtomValue(tradeStepAtom)
  const isUserRejectionError = useAtomValue(isUserRejectionErrorAtom)
  const isWarning = tradeStep.error && isUserRejectionError
  const route = useAtomValue(routeAtom)
  const snapshot = useAtomValue(routeMachineSnapshotAtom)
  const refundInfo = snapshot.context.refundInfo

  // If prior steps were aborted, this step should not be displayed
  if (
    (route?.abortedStepNumber && step.step_number > route.abortedStepNumber) ||
    (refundInfo && step.step_number > refundInfo.stepNumber)
  ) {
    return null
  }

  return (
    <BasicStepRow
      title={<Title step={step} isError={tradeStep.isError} />}
      isActive={tradeStep.isActive}
      isLoading={tradeStep.isLoading}
      isError={tradeStep.isError}
      isWarning={isWarning}
      isWaiting={tradeStep.isUserSigning}
      isSuccess={tradeStep.isSuccess}
      transferDetails={tradeStep.transferDetails}
      className={className}
    />
  )
}
